import React, { useCallback, useEffect, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { getSecrets } from "../../config";
import { useAppDispatch } from "../../hooks";

import { parseMessage } from "protico-sdk";
import {
  updateRoomMessages,
  updateRoomTyping,
} from "../../store/reducers/dmState";
import useCryptography from "../../hooks/useCryptography";
import { TMessage } from "../../types/types";
import { useWeb3Auth } from "../../hooks/useWeb3Auth";
interface IDMWebsocketContext {
  sendTyping: (toAddress: string) => void;
  sendStopTyping: (toAddress: string) => void;
  sendMessage: (message: TMessage) => void;
  sendDMListing: (
    message: TMessage,
    data: any,
    sign: any,
    hash: any,
    chainId: string
  ) => void;
  readyState: ReadyState;
}

export const DMWebsocketContext = React.createContext<IDMWebsocketContext>({
  sendTyping: () => {},
  sendStopTyping: () => {},
  sendMessage: () => {},
  sendDMListing: () => {},
  readyState: ReadyState.CONNECTING,
});

export const DMWebsocketProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  //Public API that will echo messages sent to it back to the client
  const { NODE_ENV } = getSecrets();
  const [socketUrl, setSocketUrl] = useState(null);
  const { sendJsonMessage, lastMessage, readyState } = useWebSocket(socketUrl, {
    onOpen: () => console.log("dm socket opened"),
    //Will attempt to reconnect on all close events, such as server shutting down
    shouldReconnect: (closeEvent) => true,
    reconnectAttempts: 10,
    //attemptNumber will be 0 the first time it attempts to reconnect, so this equation results in a reconnect pattern of 1 second, 2 seconds, 4 seconds, 8 seconds, and then caps at 10 seconds until the maximum number of attempts is reached
    reconnectInterval: (attemptNumber) =>
      Math.min(Math.pow(2, attemptNumber) * 1000, 10000),
  });
  const { userKeypair, decrypt } = useCryptography();
  const dispatch = useAppDispatch();
  const { isProticoAuthenticated, address } = useWeb3Auth();

  useEffect(() => {
    if (!address) return;
    if (!isProticoAuthenticated) return;
    if (!userKeypair) return;
    setSocketUrl(
      (NODE_ENV === "production" ? "wss://" : "ws://") +
        window.location.host +
        "/ws/dm/" +
        address
    );
  }, [isProticoAuthenticated, address, userKeypair]);

  useEffect(() => {
    if (lastMessage !== null) {
      const data = JSON.parse(lastMessage.data);
      if (data.type === "typing") {
        dispatch(
          updateRoomTyping({
            toAddress: data.payload.address,
            isOtherTyping: true,
          })
        );
      } else if (data.type === "stop_typing") {
        dispatch(
          updateRoomTyping({
            toAddress: data.payload.address,
            isOtherTyping: false,
          })
        );
      } else if (data.type === "message") {
        // Get a message from the server
        const message = parseMessage(data.payload);
        const fromOther = address === message.toAddress;
        let plaintextMessage: string = "";
        try {
          plaintextMessage = decrypt(message);
        } catch (e) {
          console.log("decrypt fail: ", e);
        }
        if (plaintextMessage) {
          dispatch(
            updateRoomMessages({
              toAddress: fromOther ? message.fromAddress : message.toAddress,
              message: {
                ...message,
                plaintextMessage,
              },
            })
          );
        }
      } else if (data.type === "new_list") {
        // Get a message from the server
        const message = parseMessage(data.payload);
        const fromOther = address === message.toAddress;
        let plaintextMessage: string = "";
        try {
          plaintextMessage = decrypt(message);
        } catch (e) {
          console.log("decrypt fail: ", e);
        }
        if (plaintextMessage) {
          dispatch(
            updateRoomMessages({
              toAddress: fromOther ? message.fromAddress : message.toAddress,
              message: {
                ...message,
                plaintextMessage,
              },
            })
          );
        }
      }
    }
  }, [lastMessage]);

  const sendTyping = useCallback(
    (toAddress: string) =>
      sendJsonMessage({
        type: "typing",
        payload: { to_address: toAddress },
      }),
    []
  );
  const sendStopTyping = useCallback(
    (toAddress: string) =>
      sendJsonMessage({
        type: "stop_typing",
        payload: { to_address: toAddress },
      }),
    []
  );
  const sendMessage = useCallback(
    (message: TMessage) =>
      sendJsonMessage({
        type: "message",
        payload: {
          id: message.id,
          from_address: message.fromAddress,
          to_address: message.toAddress,
          message_text: message.messageText,
          msg_sha256_base64: message.msgSha256Base64,
          from_pub_key_base64: message.fromPubKeyBase64,
          to_pub_key_base64: message.toPubKeyBase64,
        },
      }),
    []
  );
  const sendDMListing = useCallback(
    (message: TMessage, data: any, sign: any, hash: any, chainId: string) =>
      sendJsonMessage({
        type: "new_list",
        payload: {
          id: message.id,
          from_address: message.fromAddress,
          to_address: message.toAddress,
          message_text: message.messageText,
          msg_sha256_base64: message.msgSha256Base64,
          from_pub_key_base64: message.fromPubKeyBase64,
          to_pub_key_base64: message.toPubKeyBase64,
        },
        typeddata: {
          data: data,
          sign: sign,
          hash: hash,
          chainId: chainId,
        },
      }),
    []
  );

  return (
    <DMWebsocketContext.Provider
      value={{
        sendTyping,
        sendStopTyping,
        sendMessage,
        sendDMListing,
        readyState,
      }}
    >
      {children}
    </DMWebsocketContext.Provider>
  );
};
